import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { AgentPanelService } from '@wephone/services/agent-panel.service';
import { AgentEntity } from '@wephone-core/model/entity/agent';
import { PauseReasonEntity } from '@wephone-core/model/entity/pause_reason';
import { PauseReasonRepository } from '@wephone-core/model/repository/pause_reason';
import { AgentRepository } from '@wephone-core/model/repository/agent';
import { MessageService, DialogService, Platform, ToastService, LoggerService } from '@wephone-utils';
import { MyCallCenterStatus } from '@wephone-core/model/entity/my_callcenter_status';
import { AgentPhoneMode, AgentInOutState, EnterpriseParam, EnterpriseParamDict, UserType } from '@wephone-core/system';
import { UserEntity } from '@wephone-core/model/entity/user';
import { AgentPanelSettingComponent } from '@wephone-app-phone/modals/settings/agent-panel-settings/agent-panel-settings';
import { introJs } from 'intro.js/minified/intro.min.js';
import { StorageService } from '@wephone-core/service/storage.service';
import { PauseAgentDialog, UserCallReceptionDialog } from '@wephone-common/modals';
import { PhoneCall, PhoneService } from '@wephone-app-phone/services';
import { _tk, _ti } from '@wephone-translation';
import { CallingNumber, UserProfileService } from '@wephone/services/user-profile.service';
import { IJsPhoneConfig } from '@jsphonelib';
import { ConfigManager } from '@wephone-core/wephone-core.module';
import { MatSelectChange } from '@angular/material/select';
import { CallQueueEntity } from '@wephone-core/model/entity/callqueue';
import { CallQueueRepository } from '@wephone-core/model/repository/callqueue';
import { Subscription } from 'rxjs';
import { SingleUserStatsQoSData, UserService } from '@wephone/services/user.service';
import { TransferConfirmModal } from '@wephone-app-phone/modals/transfer-confirm/transfer-confirm';
import { TransferDestinationType, TransferRequest } from '@wephone-core/core/call_transfer';
import { AgentService, IDailyStats } from '@wephone/services/agent.service';
import { TodayStatsModalComponent } from '@wephone-app-phone/modals/today-stats-modal/today-stats-modal.component';
import * as _ from 'lodash';

enum UI_MODE {
  NORNAL = 'NORMAL',
  DIALING = 'DIALING',
}

enum CONNECTION_STATE {
  STATE_ONHOLD = 'on_hold',
  STATE_CALL_PROGRESSING = 'call_progressing',
  STATE_INCALL = 'in_call',
  STATE_PAUSED = 'paused',
  STATE_IDLE = 'idle',
  STATE_DISCONNECTED = 'disconnected',
}

@Component({
  selector: 'agent-panel',
  templateUrl: 'agent-panel.html',
  styleUrls: ['./agent-panel.scss']
})
export class AgentPanelComponent implements OnInit, OnDestroy {
  userDisplayName: string;
  userTypeName: string;
  directionSelectable = 0;
  session_info: any = {};
  pause_reasons: PauseReasonEntity[];
  agent: AgentEntity;
  isAgent: boolean;
  uiMode: UI_MODE = UI_MODE.NORNAL;
  user: UserEntity;
  show = true;
  inOutStateList: AgentInOutState[] = [AgentInOutState.BOTH, AgentInOutState.INBOUND, AgentInOutState.OUTBOUND];
  isElectron = false;
  phoneConfig: IJsPhoneConfig;
  selectedReasonId: number;
  inboundOnSecondaryNumber: number;

  allowSelectQueueForOutbound: boolean;
  callQueueForCalling: CallQueueEntity;
  phoneCallingNumber: CallingNumber;

  todayPerformance: SingleUserStatsQoSData;
  userDailyStats: IDailyStats;
  lastDefaultQueueId: number;
  hasDailyStats: boolean;

  private readonly iFrameCommands = ['call_transfer'];

  private nextDailyStatsCallTimerId: any;
  private subscriptionList: Subscription[] = [];

  sendRpcAck(source: Window, rpc_id: string): void {
    if (source && rpc_id) {
      source.postMessage(
        {
          message: 'rpc_ack',
          protocol: 'wephone-bo-push',
          data: {
            rpc_id
          }
        },
        '*'
      );
    }
  }

  sendRpcReply(source: Window, rpc_id: string, success: boolean, result: string): void {
    if (source && rpc_id) {
      source.postMessage(
        {
          message: 'rpc_reply',
          protocol: 'wephone-bo-push',
          data: {
            rpc_id,
            success,
            result
          }
        },
        '*'
      );
    }
  }

  @HostListener('window:message', ['$event'])
  onMessage(e: { data: any, origin: string, source: Window }): void {
    let protocolVersion = '1.0';
    let postData;
    let rpc_id: string;
    let functionName: string;
    let functionParams: any;
    try {
      postData = JSON.parse(e.data);
    } catch (e) {
      return;
    }

    if (postData.rpc_version) {
      protocolVersion = postData.rpc_version;
    }

    functionName = postData && postData.function; // backward compatible

    if (protocolVersion === '1.0') {
      functionParams = postData; // backward compatible  
    } else if (protocolVersion === '2.0') {
      functionParams = postData.params;
      rpc_id = postData.rpc_id;
      this.sendRpcAck(e.source, rpc_id);
    }

    if (!functionName || this.iFrameCommands.indexOf(functionName) === -1) {
      this.sendRpcReply(e.source, rpc_id, false, `Invalid function: ${functionName} `);
      return;
    }

    const activeCall = this.phoneService.getActiveCall() as PhoneCall;
    if (!activeCall) {
      this.loggerService.error('No current session');
      this.toastService.showError(_ti('callcenter_agent_panel.message.no_call'));
      this.sendRpcReply(e.source, rpc_id, false, 'No active call');
      return;
    }

    if (functionParams.transfer_type === 'application') {
      if (!functionParams.app_name) {
        this.loggerService.error('Invalid app_name param for call transfer request');
        this.toastService.showError('callcenter_agent_panel.message.transfer_failure');
        this.sendRpcReply(e.source, rpc_id, false, 'Missing app_name parameter');
        return;
      }
      const appTransferReq = new TransferRequest(TransferDestinationType.APPLICATION, functionParams.app_name, false, undefined, functionParams.app_params);
      this.phoneService.transferCall(activeCall, appTransferReq).then(
        res => {
          // this.loggerService.log('Transfer success', res);
          this.toastService.showSuccess(_ti('callcenter_agent_panel.message.transfer_success'));
          this.sendRpcReply(e.source, rpc_id, true, 'transfer_success');
        },
        error => {
          this.loggerService.error('Transfer error', error);
          this.sendRpcReply(e.source, rpc_id, false, 'transfer_failure');
          this.toastService.showError('callcenter_agent_panel.message.transfer_failure');
        }
      );
      return;
    }

    const transferNumber = functionParams.number;
    const assistedTransfer = functionParams.assisted || false;
    const transferRequest = new TransferRequest(TransferDestinationType.NUMBER, transferNumber, assistedTransfer);

    this.dialogService.openDialog2(TransferConfirmModal, { data: {}, size: 's' }, result => {
      if (!result) {
        return;
      }

      transferRequest.transfer_reason = result.transfer_reason;
      this.phoneService.transferCall(activeCall, transferRequest).then(
        res => {
          // this.loggerService.log('Transfer success', res);
          this.toastService.showSuccess(_ti('callcenter_agent_panel.message.transfer_success'));
          this.sendRpcReply(e.source, rpc_id, true, 'transfer_success');
        },
        error => {
          this.loggerService.error('Transfer error', error);
          this.toastService.showError('callcenter_agent_panel.message.transfer_failure');
          this.sendRpcReply(e.source, rpc_id, false, 'transfer_failure');
        }
      );
    });

    this.sendRpcReply(e.source, rpc_id, true, 'transfer_inprogress');
  }

  constructor(
    platform: Platform,
    public readonly messageService: MessageService,
    public readonly authService: AuthenticationService,
    private readonly agentService: AgentService,
    public readonly agentPanelService: AgentPanelService,
    private readonly toastService: ToastService,
    private readonly dialogService: DialogService,
    private readonly storage: StorageService,
    private readonly phoneService: PhoneService,
    private readonly config: ConfigManager,
    private readonly userProfileService: UserProfileService,
    private readonly userService: UserService,
    private readonly loggerService: LoggerService,
  ) {
    this.agentPanelService.enableAutoReload(1000);
    this.isElectron = platform.isElectronApp();
  }

  async ngOnInit(): Promise<void> {
    this.userDisplayName = this.authService.getUserDisplayName();

    this.authService.getUserType().then(userTypeName => {
      this.userTypeName = userTypeName;
    });

    this.isAgent = this.authService.isAgent();

    this.user = this.authService.getUser();
    const agentId: number = this.authService.getAgentId();
    this.agent = AgentRepository.getInstance<AgentRepository>().getObjectById(agentId);
    this.pause_reasons = PauseReasonRepository.getInstance<PauseReasonRepository>().getActiveObjectList();
    this.directionSelectable = this.config.getEnterpriseParamValue(EnterpriseParam.direction_selection);

    const allowedUserTypes = [UserType.SUPER_ADMIN, UserType.ADMIN, UserType.SUPERVISOR, UserType.WATCHER, UserType.WATCHER, UserType.AGENT];
    this.hasDailyStats = _.includes(allowedUserTypes, this.user.user_type);

    const queueRepo: CallQueueRepository = CallQueueRepository.getInstance<CallQueueRepository>();
    const enterpriseParams: EnterpriseParamDict = this.config.getEnterpriseParams();
    const agentQueues: CallQueueEntity[] = queueRepo.getInboundQueuesByAgentId(agentId);

    this.inboundOnSecondaryNumber = this.config.getEnterpriseParamValue(EnterpriseParam.inbound_on_secondary_number);

    // tslint:disable-next-line:max-line-length
    this.allowSelectQueueForOutbound = enterpriseParams && !!enterpriseParams.dialer_queue_selector && agentQueues.length > 0 || false;
    this.phoneService.loadPhoneConfig().then((phoneConfig: IJsPhoneConfig) => {
      this.phoneConfig = phoneConfig || { silent_mode: false };
      // this.loggerService.log('phone config phoneConfig response', phoneConfig);
    });

    if (this.allowSelectQueueForOutbound) {
      this.callQueueForCalling = await this.userProfileService.getDefaultCallQueue();
    }

    this.phoneCallingNumber = await this.userProfileService.getDefaultCallingNumber();

    this.subscriptionList.push(
      this.messageService.subscribe(this.agentPanelService.notifications.AGENTPANEL_DATA_UPDATE, () => {
        this.session_info = this.agentPanelService.session_info;
      })
    );

    if (this.hasDailyStats) {
      this.scheduleNextDailyStatsCall();
    }
  }

  ngOnDestroy(): void {
    if (this.nextDailyStatsCallTimerId) {
      clearInterval(this.nextDailyStatsCallTimerId);
    }

    for (const sub of this.subscriptionList) {
      sub.unsubscribe();
    }
    this.subscriptionList = [];
  }

  private async scheduleNextDailyStatsCall(): Promise<void> {
    await this.setDailyStatsCall();

    this.nextDailyStatsCallTimerId = setInterval(() => {
      this.setDailyStatsCall();
    },
      15000
    );
  }

  private async setDailyStatsCall(): Promise<void> {
    this.userDailyStats = await this.agentService.queueUserDailyStats();

    this.todayPerformance = await this.userService.getUserPerformance();
  }

  get connection_status(): MyCallCenterStatus {
    return this.agentService.getMyCallcenterStatus();
  }

  get inOutState(): AgentInOutState {
    return this.connection_status.inout_state;
  }

  set inOutState(inOutState: AgentInOutState) {
    this.changeInoutState(inOutState);
  }

  private async changeInoutState(inOutState: AgentInOutState): Promise<any> {
    try {
      if (inOutState !== this.connection_status.inout_state) {
        await this.agentService.set_inout_state(this.connection_status.id, inOutState);
        this.toastService.showSuccess(_ti('public.message.update_success'));
      }
    } catch (e) {
      this.loggerService.error('Cannot change inOutState', e);
      this.showExceptionMessage(e, _ti('public.message.update_failure'));
    }
  }

  async changeCallingQueue(callQueueForCalling: CallQueueEntity): Promise<void> {
    // this.loggerService.log('changeCallingQueue value', callQueueForCalling);
    try {
      await this.userProfileService.setDefaultQueue(callQueueForCalling.id);
      this.phoneCallingNumber = this.userProfileService.getMyDefaultCallingDID();
    } catch (e) {
      this.loggerService.error('Cannot set calling queue', e);
    }
  }

  async changeCallingNumber($event: MatSelectChange): Promise<void> {
    const selected: CallingNumber = $event.value;
    // this.loggerService.log('changeCallingNumber value', selected);
    if (!selected) {
      this.loggerService.error('No calling-number selected');
      return;
    }

    try {
      await this.userProfileService.setDefaultCallingNumber(selected);
      // this.toastService.show(_ti('public.message.update_success'));
    } catch (e) {
      this.loggerService.error('Cannot set calling number', e);
      // this.loggerService.error('Cannot update calling_number_id', e);
      // this.showExceptionMessage(e, _ti('public.message.update_failure'));
    }
  }

  async connectCallCenter(inOutState = 0): Promise<any> {
    // this.loggerService.log('connectCallCenter: inOutState=', inOutState);
    if (!this.connection_status.id) {
      this.loggerService.error('Agent not connected');

      return undefined;
    }
    try {
      const ret = await this.agentPanelService.connect_callcenter(this.connection_status.id, inOutState);
      // this.loggerService.log('connectCallCenter success', ret);
      this.toastService.show(_ti('agent_panel.action_result.connection_success'));
      this.show = true;

      return ret;
    } catch (e) {
      this.loggerService.error('connectCallCenter error', e);
      this.showExceptionMessage(e, _ti('agent_panel.action_result.connect_failure'));
    }
  }

  // isPhoneNumberConnected(): boolean {
  //   return !!(this.agent && this.agent.sipphone && this.agent.sipphone.is_online);
  // }

  // getAgentPhoneNumber(): string {
  //   let agent_phone: string;
  //   if (this.connection_status) {
  //     switch (this.connection_status.phone_mode) {
  //       case AgentPhoneMode.SIP_PHONE:
  //         agent_phone = this.agent && this.agent.sipphone && this.agent.sipphone.extension;
  //         break;
  //       case AgentPhoneMode.BACKUP_NUMBER:
  //         agent_phone = this.agent && this.agent.user && this.agent.user.phone;
  //         break;
  //       case AgentPhoneMode.BOTH:
  //         agent_phone = this.agent.phone_number;
  //         break;
  //       default:
  //         break;
  //     }
  //   }

  //   return agent_phone;
  // }

  private isOnSipphone(): boolean {
    return this.connection_status && this.connection_status.phone_mode === AgentPhoneMode.SIP_PHONE;
  }

  // changeVolumn(): void {
  //   this.audioDeviceService.openAudioDeviceSettings();
  // }

  async unpause(): Promise<any> {
    if (!this.connection_status.is_paused) {
      this.loggerService.error('Agent is in unpause');
      return;
    }

    // if (this.connection_status.id) {
    //   return this.agentPanelService.unpause(this.connection_status.id);
    // }
    try {
      await this.agentService.unpause(this.authService.getAgentId());
      this.toastService.showSuccess(_ti('agent_panel.message.unpause_success'));
    } catch (response) {
      this.showExceptionMessage(response, _ti('agent_panel.message.unpause_failure'));
    }
  }

  pause(): void {
    if (this.connection_status.is_paused) {
      this.loggerService.error('Agent is in pause');
      return;
    }

    this.dialogService.openDialog2(PauseAgentDialog, { hasBackdrop: true }, async (data: { pause_reason_id: number }) => {
      if (!data || !data.pause_reason_id) {
        this.loggerService.error('No selected pause reason');
        return;
      }

      const reason_id = data.pause_reason_id;
      try {
        await this.agentService.pause(this.authService.getAgentId(), reason_id);
        this.selectedReasonId = reason_id;
        this.toastService.showSuccess(_ti('agent_panel.message.pause_success'));
      } catch (response) {
        this.showExceptionMessage(response, _ti('agent_panel.message.pause_failure'));
      }
    });
  }

  private showExceptionMessage(errorException: any, defaultMessage = _ti('public.message.error_occurred')): void {
    let message: string;
    if (errorException) {
      message = errorException.error && errorException.error.message || errorException.message;
    }
    this.toastService.showError(message || defaultMessage);
  }

  // state_idle(): boolean {
  //   return !this.session_info.session_keyid && !this.connection_status.is_paused;
  // }

  // Not in-used
  // disconnectCallCenter(): void {
  //   if (!this.connection_status.id) {
  //     this.loggerService.error('Agent not connected');

  //     return;
  //   }
  //   AgentPanelService.getInstance().disconnect_callcenter(this.connection_status.id).then(
  //     response => {
  //       this.loggerService.log('disconnectCallCenter success', response);
  //       this.toastService.show(_ti('agent_panel.action_result.disconnect_success'));
  //       this.show = false;
  //     },
  //     response => {
  //       this.loggerService.error('disconnectCallCenter error', response);
  //       this.toastService.showError(_ti('agent_panel.action_result.disconnect_failure'));
  //     }
  //   );
  // }

  // holdCall(): void {
  //   this.loggerService.log('holdCall');
  //   AgentPanelService.getInstance().hold(this.session_info.session_keyid).then(
  //     response => {
  //       this.loggerService.log('hold success', response);
  //       this.toastService.show(_ti('agent_panel.action_result.hold_success'));
  //     },
  //     response => {
  //       this.loggerService.error('hold error', response);
  //       this.toastService.showError(_ti('agent_panel.action_result.hold_failure'));
  //     }
  //   );
  // }

  // resumeCall(): void {
  //   this.loggerService.log('resumeCall');
  //   AgentPanelService.getInstance().resume(this.session_info.session_keyid).then(
  //     response => {
  //       this.loggerService.log('resume success', response);
  //       this.toastService.show(_ti('agent_panel.action_result.resume_success'));
  //     },
  //     response => {
  //       this.loggerService.error('resume error', response);
  //       this.toastService.showError(_ti('agent_panel.action_result.resume_failure'));
  //     }
  //   );
  // }

  openChangeReceiveCallNumberDialog(): void {
    const dialogRef = this.dialogService.openDialog2(UserCallReceptionDialog, {
      data: this.connection_status, size: 'xs'
    });
  }

  get agent_connection_state(): CONNECTION_STATE {
    let state = CONNECTION_STATE.STATE_IDLE;
    if (!this.connection_status.is_connected) {
      state = CONNECTION_STATE.STATE_DISCONNECTED;
    } else if (this.connection_status.is_paused) {
      state = CONNECTION_STATE.STATE_PAUSED;
    }

    return state;
  }

  openAgentPanelSettings(): void {
    this.dialogService.openDialog2(AgentPanelSettingComponent, {});
  }

  startTour(): void {
    const intro = introJs();
    intro.setOption('tooltipPosition', 'auto');
    intro.setOption('positionPrecedence', ['left', 'right', 'bottom', 'top']);
    intro.addStep({ intro: 'Welcome to Graam' });

    if (this.isOnSipphone()) {
      intro.addStep({
        element: '#change-receive-number',
        intro: 'Click here to change receive call number',
        position: 'bottom'
      });
    }
    intro.addStep({
      element: '#pause',
      intro: 'Click here to pause call center',
      position: 'bottom'
    });
    if (this.connection_status.is_connected) {
      intro.addStep({
        element: '#disconnect',
        intro: 'Disconnect call center',
        position: 'bottom'
      });
    }
    intro.onskip(() => {
      this.disableTour();
    });
    intro.oncomplete(() => {
      this.disableTour();
    });
    intro.onexit(() => {
      this.disableTour();
    });
    intro.start();
  }

  private disableTour(): void {
    this.storage.setDomainConfig('agentpanel_tour', { disabled: 1 });
  }

  changeSilentMode(): void {
    this.phoneConfig.silent_mode = !this.phoneConfig.silent_mode;
    this.phoneService.applyPhoneConfig(this.phoneConfig);
  }

  openTodayStats(): void {
    this.dialogService.openDialog2(TodayStatsModalComponent, { data: { hasLoadData: true } });
  }
}
